/**
 * Icon packs: pack > group > icon, each with an id that never changes.
 *
 * What a record stores in `icon` is an icon ref: the bare iconId for the Classic pack (so every icon saved before
 * packs existed, e.g. "sheet", is still the same Classic icon), and "<packId>/<iconId>" for any later pack.
 * Groups only help people find an icon; they are not saved and do not decide a category's layout.
 * `tier` is there for paid packs later; this release has only the free Classic pack and nothing is locked.
 */
export type PackTier = "free" | "premium";
export interface IconDef { iconId: string; name: string }
export interface IconGroup { groupId: string; name: string; icons: IconDef[] }
export interface IconPack { packId: string; name: string; tier: PackTier; groups: IconGroup[] }

export const CLASSIC_PACK_ID = "classic";

const g = (groupId: string, name: string, icons: [string, string][]): IconGroup =>
  ({ groupId, name, icons: icons.map(([iconId, n]) => ({ iconId, name: n })) });

export const CLASSIC: IconPack = {
  packId: CLASSIC_PACK_ID, name: "Classic", tier: "free",
  groups: [
    g("work", "งานและเอกสาร", [["note", "เอกสาร"], ["sheet", "Sheet"], ["clipboard", "คลิปบอร์ด"], ["calculator", "เครื่องคิดเลข"],
      ["briefcase", "กระเป๋างาน"], ["chart", "กราฟ"], ["calendar", "ปฏิทิน"], ["book", "หนังสือ"]]),
    g("links", "ลิงก์และสื่อสาร", [["link", "ลิงก์"], ["globe", "เว็บไซต์"], ["mail", "อีเมล"], ["chat", "แชท"], ["phone", "โทรศัพท์"]]),
    g("places", "สถานที่และผู้คน", [["home", "บ้าน"], ["office", "ออฟฟิศ"], ["pin", "หมุด"], ["user", "บุคคล"], ["users", "ทีม"]]),
    g("lifestyle", "ไลฟ์สไตล์", [["coffee", "กาแฟ"], ["heart", "สุขภาพ"], ["food", "อาหาร"], ["travel", "ท่องเที่ยว"],
      ["fitness", "ออกกำลังกาย"], ["pulse", "ชีพจร"]]),
    g("media", "ความบันเทิง", [["video", "วิดีโอ"], ["music", "เพลง"], ["film", "ภาพยนตร์"], ["game", "เกม"]]),
    g("shop", "ช้อปปิ้ง", [["bag", "ถุงช้อปปิ้ง"], ["cart", "ตะกร้า"], ["store", "ร้านค้า"], ["tag", "ป้ายราคา"], ["gift", "ของขวัญ"],
      ["wallet", "กระเป๋าเงิน"]]),
    g("inspiration", "Inspiration — แรงบันดาลใจ", [["bulb", "ไอเดีย"], ["design", "งานออกแบบ"], ["camera", "ภาพถ่าย"], ["art", "ศิลปะ"],
      ["bookmark", "บุ๊กมาร์ก"], ["sparkles", "ประกาย"]]),
    g("other", "อื่น ๆ", [["folder", "โฟลเดอร์"], ["star", "ดาว"], ["key", "กุญแจ"], ["code", "โค้ด"], ["grid", "กริด"]])
  ]
};

export const ICON_PACKS: readonly IconPack[] = [CLASSIC];

export const iconRef = (packId: string, iconId: string) => packId === CLASSIC_PACK_ID ? iconId : `${packId}/${iconId}`;

export interface IconHit { pack: IconPack; group: IconGroup; icon: IconDef; ref: string }
/** pack, group and name of a stored icon ref; null when no pack has it */
export function findIcon(ref: string): IconHit | null {
  const slash = ref.indexOf("/");
  const packId = slash < 0 ? CLASSIC_PACK_ID : ref.slice(0, slash), iconId = slash < 0 ? ref : ref.slice(slash + 1);
  const pack = ICON_PACKS.find(p => p.packId === packId); if (!pack) return null;
  for (const group of pack.groups) {
    const icon = group.icons.find(i => i.iconId === iconId);
    if (icon) return { pack, group, icon, ref };
  }
  return null;
}

/** every icon ref the data may hold, in picker order */
export const ALL_ICON_REFS: readonly string[] = ICON_PACKS.flatMap(p => p.groups.flatMap(gr => gr.icons.map(i => iconRef(p.packId, i.iconId))));
